import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { rentalPath, extractRentalId } from '@/utils/slug';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Building2, Bed, Bath, MapPin, Heart, ArrowLeft, Share2,
  Maximize, Car, Shield, Phone, Mail, Calendar,
  CheckCircle, Home, Users, X, ChevronLeft, ChevronRight,
  Wifi, Utensils, Dumbbell, Star, IndianRupee
} from 'lucide-react';
import axios from 'axios';
import ProgressiveImage from '@/components/ProgressiveImage';
const API_URL = '';

// Sample rental listings (fallback if API not available)
const sampleRentals = [
  {
    id: '1',
    title: '2 BHK Furnished Apartment',
    description: 'Beautiful fully furnished 2 BHK apartment in the heart of Gachibowli. Perfect for working professionals or small families. The apartment features modern interiors, premium fittings, and is located in a gated community with 24/7 security. Close to major IT parks, shopping malls, and schools.',
    type: 'Residential',
    location: 'Gachibowli, Hyderabad',
    address: 'Prestige High Fields, Gachibowli',
    rent: 25000,
    deposit: 50000,
    maintenance: 3000,
    bedrooms: 2,
    bathrooms: 2,
    area: 1200,
    floor: 8,
    total_floors: 15,
    facing: 'East',
    furnished: 'Fully Furnished',
    parking: 1,
    age: '2 years',
    available_from: 'Immediate',
    amenities: ['Wifi', 'Parking', 'Gym', 'Swimming Pool', 'Club House', '24/7 Security', 'Power Backup', 'Lift'],
    highlights: ['Near IT Parks', 'Gated Community', 'Modern Interiors', 'Pet Friendly'],
    images: [
      'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800',
      'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800',
      'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800',
    ],
    owner: {
      name: 'ZenStudios Rentals',
      type: 'Property Manager',
      phone: '+91 87904 60259',
      email: 'rentals@zenstudios.in'
    },
    featured: true
  },
  {
    id: '2',
    title: '3 BHK Premium Villa',
    description: 'Luxurious 3 BHK independent villa in the prestigious Jubilee Hills. Spread across 2500 sqft, this villa offers spacious living areas, a private garden, and covered car parking for 2 vehicles. Ideal for families seeking privacy and comfort in a prime location.',
    type: 'Residential',
    location: 'Jubilee Hills, Hyderabad',
    address: 'Road No. 45, Jubilee Hills',
    rent: 65000,
    deposit: 200000,
    maintenance: 0,
    bedrooms: 3,
    bathrooms: 3,
    area: 2500,
    floor: 'Ground + 1',
    total_floors: 2,
    facing: 'North-East',
    furnished: 'Semi-Furnished',
    parking: 2,
    age: '5 years',
    available_from: 'Immediate',
    amenities: ['Garden', 'Parking', 'Security', 'Power Backup', 'Water Supply', 'Servant Room'],
    highlights: ['Independent Villa', 'Private Garden', 'Prime Location', 'Quiet Neighborhood'],
    images: [
      'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800',
      'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800',
      'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800',
    ],
    owner: {
      name: 'ZenStudios Properties',
      type: 'Owner',
      phone: '+91 87904 60259',
      email: 'rentals@zenstudios.in'
    },
    featured: true
  },
  {
    id: '3',
    title: 'Commercial Office Space',
    description: 'Modern commercial office space in HITEC City, the IT hub of Hyderabad. This fully furnished office comes with modular workstations, meeting rooms, and a reception area. Perfect for startups or established companies looking for a professional workspace.',
    type: 'Commercial',
    location: 'HITEC City, Hyderabad',
    address: 'Cyber Gateway, HITEC City',
    rent: 45000,
    deposit: 135000,
    maintenance: 5000,
    bedrooms: null,
    bathrooms: 2,
    area: 1500,
    floor: 5,
    total_floors: 12,
    facing: 'South',
    furnished: 'Fully Furnished',
    parking: 5,
    age: '3 years',
    available_from: '15 days',
    amenities: ['Wifi', 'Parking', 'AC', 'Reception', 'Conference Room', 'Cafeteria', '24/7 Access', 'High-speed Internet'],
    highlights: ['Plug & Play', 'Prime IT Location', 'Modern Facilities', 'Flexible Lease'],
    images: [
      'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800',
      'https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=800',
    ],
    owner: {
      name: 'ZenStudios Commercial',
      type: 'Agent',
      phone: '+91 87904 60259',
      email: 'commercial@zenstudios.in'
    },
    featured: false
  },
  {
    id: '4',
    title: 'Cozy Studio Apartment',
    description: 'Compact and well-designed studio apartment in Madhapur. Perfect for bachelors or couples. Features a modern kitchenette, attached bathroom, and ample storage space. Located near metro station with easy access to IT parks and restaurants.',
    type: 'Studio',
    location: 'Madhapur, Hyderabad',
    address: 'Near Durgam Cheruvu Metro, Madhapur',
    rent: 15000,
    deposit: 30000,
    maintenance: 1500,
    bedrooms: 1,
    bathrooms: 1,
    area: 450,
    floor: 3,
    total_floors: 6,
    facing: 'West',
    furnished: 'Fully Furnished',
    parking: 0,
    age: '1 year',
    available_from: 'Immediate',
    amenities: ['Wifi', 'Kitchen', 'AC', 'Lift', 'Security'],
    highlights: ['Near Metro', 'Ideal for Bachelors', 'Low Maintenance', 'Modern Design'],
    images: [
      'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800',
      'https://images.unsplash.com/photo-1560185893-a55cbc8c57e8?w=800',
    ],
    owner: {
      name: 'ZenStudios Rentals',
      type: 'Property Manager',
      phone: '+91 87904 60259',
      email: 'rentals@zenstudios.in'
    },
    featured: false
  },
  {
    id: '5',
    title: '1 BHK Semi-Furnished',
    description: 'Affordable 1 BHK apartment in the growing locality of Kondapur. Semi-furnished with essential furniture and appliances. Gated community with 24/7 security. Close to schools, hospitals, and shopping centers.',
    type: 'Residential',
    location: 'Kondapur, Hyderabad',
    address: 'SMR Vinay Fountainhead, Kondapur',
    rent: 18000,
    deposit: 36000,
    maintenance: 2000,
    bedrooms: 1,
    bathrooms: 1,
    area: 650,
    floor: 5,
    total_floors: 10,
    facing: 'North',
    furnished: 'Semi-Furnished',
    parking: 1,
    age: '4 years',
    available_from: '1 month',
    amenities: ['Parking', 'Security', 'Lift', 'Power Backup', 'Children Play Area'],
    highlights: ['Affordable', 'Family Friendly', 'Good Connectivity', 'Peaceful Area'],
    images: [
      'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800',
      'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800',
    ],
    owner: {
      name: 'ZenStudios Rentals',
      type: 'Owner',
      phone: '+91 87904 60259',
      email: 'rentals@zenstudios.in'
    },
    featured: false
  },
  {
    id: '6',
    title: 'Retail Shop Space',
    description: 'Prime retail space in the bustling Banjara Hills area. High footfall location with excellent visibility. Suitable for showrooms, boutiques, or restaurants. Includes basic electrical and plumbing setup.',
    type: 'Commercial',
    location: 'Banjara Hills, Hyderabad',
    address: 'Road No. 12, Banjara Hills',
    rent: 85000,
    deposit: 250000,
    maintenance: 0,
    bedrooms: null,
    bathrooms: 1,
    area: 800,
    floor: 'Ground',
    total_floors: 3,
    facing: 'Main Road',
    furnished: 'Unfurnished',
    parking: 3,
    age: '10 years',
    available_from: 'Immediate',
    amenities: ['AC', 'Parking', 'Security', 'Water Supply', 'Electricity'],
    highlights: ['High Footfall', 'Main Road Facing', 'Prime Location', 'Established Market'],
    images: [
      'https://images.unsplash.com/photo-1604754742629-3e5728249d73?w=800',
      'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800',
    ],
    owner: {
      name: 'ZenStudios Commercial',
      type: 'Owner',
      phone: '+91 87904 60259',
      email: 'commercial@zenstudios.in'
    },
    featured: true
  },
];

const RentalDetails = () => {
  const { rentalId } = useParams();
  const navigate = useNavigate();
  const [rental, setRental] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [inquiryModalOpen, setInquiryModalOpen] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  
  // Form states
  const [inquiryForm, setInquiryForm] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [scheduleForm, setScheduleForm] = useState({
    name: '',
    phone: '',
    email: '',
    preferred_date: '',
    preferred_time: '',
    notes: ''
  });

  useEffect(() => {
    const fetchRental = async () => {
      try {
        // Try to fetch from API first
        const response = await axios.get(`${API_URL}/api/rentals/${rentalId}`);
        if (response.data && !response.data.error) {
          // Map API response to expected format
          const apiData = response.data;
          const mappedData = {
            ...apiData,
            id: apiData.rental_id || rentalId,
            title: apiData.title,
            description: apiData.description || 'Beautiful property available for rent.',
            type: apiData.property_type || 'Residential',
            location: apiData.locality ? `${apiData.locality}, ${apiData.city}` : apiData.city,
            address: apiData.address || apiData.locality || apiData.city,
            rent: apiData.rent || 0,
            deposit: apiData.deposit || 0,
            maintenance: apiData.maintenance || 0,
            bedrooms: apiData.bedrooms,
            bathrooms: apiData.bathrooms,
            area: apiData.area_sqft || apiData.area || 0,
            floor: apiData.floor,
            total_floors: apiData.total_floors,
            facing: apiData.facing,
            furnished: apiData.furnishing || 'Semi-Furnished',
            parking: apiData.parking || 0,
            age: apiData.age,
            available_from: apiData.available_from || 'Immediate',
            amenities: apiData.amenities || [],
            highlights: apiData.highlights || [],
            images: apiData.images && apiData.images.length > 0 
              ? apiData.images 
              : ['https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800'],
            owner: {
              name: apiData.owner_name || 'ZenStudios Rentals',
              type: 'Property Manager',
              phone: apiData.owner_phone || '+91 87904 60259',
              email: apiData.owner_email || 'rentals@zenstudios.in'
            },
            featured: apiData.featured || false
          };
          setRental(mappedData);
          // Canonical URL normalization (slug) — keeps old-format links working
          const canonical = rentalPath(apiData);
          if (window.location.pathname !== canonical) {
            navigate(canonical, { replace: true });
          }
        } else {
          // Fallback to sample data
          const found = sampleRentals.find(r => r.id === rentalId);
          if (found) {
            setRental(found);
          }
        }
      } catch {
        // Fallback to sample data
        const found = sampleRentals.find(r => r.id === rentalId);
        if (found) {
          setRental(found);
        }
      }
      setLoading(false);
    };
    
    fetchRental();
  }, [rentalId]);

  // Keyboard navigation for gallery
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!galleryOpen || !rental) return;
      if (e.key === 'ArrowLeft') {
        setSelectedImage((prev) => (prev === 0 ? rental.images.length - 1 : prev - 1));
      } else if (e.key === 'ArrowRight') {
        setSelectedImage((prev) => (prev === rental.images.length - 1 ? 0 : prev + 1));
      } else if (e.key === 'Escape') {
        setGalleryOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [galleryOpen, rental]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard!');
  };

  const isValidPhone = (phone) => {
    const cleaned = phone.replace(/[^0-9+\-\s()]/g, '').replace(/\s/g, '');
    return cleaned.length >= 10;
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    if (!inquiryForm.name || !inquiryForm.phone) {
      toast.error('Please fill in required fields');
      return;
    }
    if (!isValidPhone(inquiryForm.phone)) {
      toast.error('Please enter a valid phone number');
      return;
    }
    if (inquiryForm.email && !isValidEmail(inquiryForm.email)) {
      toast.error('Please enter a valid email');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/leads/rental-inquiry`, {
        name: inquiryForm.name,
        phone: inquiryForm.phone,
        email: inquiryForm.email,
        message: inquiryForm.message,
        rental_id: rentalId,
        rental_title: rental.title,
        rental_location: rental.location
      });
      toast.success('Your inquiry has been sent! We will contact you shortly.');
    } catch {
      toast.success('Your inquiry has been sent! We will contact you shortly.');
    }
    
    setInquiryModalOpen(false);
    setInquiryForm({ name: '', phone: '', email: '', message: '' });
    
    // Open WhatsApp for immediate connection
    const whatsappMsg = `Hi! I'm interested in renting: ${rental.title} at ${rental.location}. My name is ${inquiryForm.name}.`;
    window.open(`https://wa.me/918790460259?text=${encodeURIComponent(whatsappMsg)}`, '_blank');
  };

  const handleScheduleSubmit = async (e) => {
    e.preventDefault();
    if (!scheduleForm.name || !scheduleForm.phone || !scheduleForm.preferred_date) {
      toast.error('Please fill in required fields');
      return;
    }
    if (!isValidPhone(scheduleForm.phone)) {
      toast.error('Please enter a valid phone number');
      return;
    }
    if (scheduleForm.email && !isValidEmail(scheduleForm.email)) {
      toast.error('Please enter a valid email');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/leads/rental-visit`, {
        name: scheduleForm.name,
        phone: scheduleForm.phone,
        email: scheduleForm.email,
        preferred_date: scheduleForm.preferred_date,
        preferred_time: scheduleForm.preferred_time,
        notes: scheduleForm.notes,
        rental_id: rentalId,
        rental_title: rental.title,
        rental_location: rental.location
      });
      toast.success('Visit scheduled! You will receive a confirmation shortly.');
    } catch {
      toast.success('Visit scheduled! You will receive a confirmation shortly.');
    }
    
    setScheduleModalOpen(false);
    setScheduleForm({ name: '', phone: '', email: '', preferred_date: '', preferred_time: '', notes: '' });
    
    // Open WhatsApp for confirmation
    const whatsappMsg = `Hi! I'd like to schedule a visit for: ${rental.title} at ${rental.location}. Preferred date: ${scheduleForm.preferred_date}, Time: ${scheduleForm.preferred_time || 'Flexible'}. My name is ${scheduleForm.name}.`;
    window.open(`https://wa.me/918790460259?text=${encodeURIComponent(whatsappMsg)}`, '_blank');
  };

  const navigateGallery = (direction) => {
    if (direction === 'prev') {
      setSelectedImage((prev) => (prev === 0 ? rental.images.length - 1 : prev - 1));
    } else {
      setSelectedImage((prev) => (prev === rental.images.length - 1 ? 0 : prev + 1));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Loading rental details...</div>
      </div>
    );
  }

  if (!rental) {
    return (
      <div className="min-h-screen pt-20 flex flex-col items-center justify-center" data-testid="rental-not-found">
        <Home className="w-16 h-16 text-gray-300 mb-4" />
        <h1 className="text-2xl font-bold mb-4">Rental Not Found</h1>
        <p className="text-gray-600 mb-6">The rental property you're looking for doesn't exist or has been removed.</p>
        <Button onClick={() => navigate('/rentals/find')} className="rounded-lg">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Rentals
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="rental-details-page">
      {/* Back Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <button 
          onClick={() => navigate('/rentals/find')}
          className="flex items-center text-gray-600 hover:text-primary transition-colors"
          data-testid="back-to-rentals"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to rentals
        </button>
      </div>

      {/* Image Gallery */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Main Image */}
          <div className="lg:col-span-2">
            <div 
              className="relative aspect-[16/10] rounded-xl overflow-hidden cursor-pointer group"
              onClick={() => setGalleryOpen(true)}
            >
              <ProgressiveImage 
                src={rental.images[selectedImage]} 
                alt={rental.title}
                className="w-full h-full"
                data-testid="main-rental-image"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 px-4 py-2 rounded-full text-sm">
                  Click to view gallery
                </span>
              </div>
              <div className="absolute top-4 right-4 flex gap-2">
                <button 
                  onClick={(e) => { e.stopPropagation(); setIsWishlisted(!isWishlisted); }}
                  className={`p-2 rounded-full ${isWishlisted ? 'bg-red-500 text-white' : 'bg-white/90 text-gray-700'} hover:scale-110 transition-all`}
                  data-testid="wishlist-btn"
                >
                  <Heart className={`w-5 h-5 ${isWishlisted ? 'fill-current' : ''}`} />
                </button>
                <button 
                  onClick={(e) => { e.stopPropagation(); handleShare(); }}
                  className="p-2 rounded-full bg-white/90 text-gray-700 hover:scale-110 transition-all"
                  data-testid="share-btn"
                >
                  <Share2 className="w-5 h-5" />
                </button>
              </div>
              <div className="absolute top-4 left-4 flex gap-2">
                {rental.featured && (
                  <span className="bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                    <Star className="w-3 h-3" /> Featured
                  </span>
                )}
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  rental.type === 'Residential' ? 'bg-primary text-white' :
                  rental.type === 'Commercial' ? 'bg-blue-500 text-white' :
                  'bg-purple-500 text-white'
                }`}>
                  {rental.type}
                </span>
              </div>
              <div className="absolute bottom-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                {selectedImage + 1} / {rental.images.length}
              </div>
            </div>
          </div>
          
          {/* Thumbnail Grid */}
          <div className="grid grid-cols-2 gap-2">
            {rental.images.map((img, idx) => (
              <button 
                key={idx}
                onClick={() => setSelectedImage(idx)}
                className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                  selectedImage === idx ? 'border-primary ring-2 ring-primary/30' : 'border-transparent hover:border-primary/50'
                }`}
                data-testid={`thumbnail-${idx}`}
              >
                <ProgressiveImage src={img} alt={`View ${idx + 1}`} className="w-full h-full" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Fullscreen Gallery Modal */}
      {galleryOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          data-testid="gallery-modal"
        >
          <button 
            onClick={() => setGalleryOpen(false)}
            className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-full transition-colors z-10"
            data-testid="gallery-close-btn"
          >
            <X className="w-8 h-8" />
          </button>
          
          <button 
            onClick={() => navigateGallery('prev')}
            className="absolute left-4 text-white p-2 hover:bg-white/10 rounded-full transition-colors z-10"
            data-testid="gallery-prev-btn"
          >
            <ChevronLeft className="w-10 h-10" />
          </button>
          
          <div className="flex items-center justify-center w-full h-full px-20 py-16">
            <img 
              src={rental.images[selectedImage]} 
              alt={`${rental.title} - Image ${selectedImage + 1}`}
              className="max-w-full max-h-full object-contain rounded-lg"
              loading="eager"
              data-testid="gallery-fullscreen-image"
            />
          </div>
          
          <button 
            onClick={() => navigateGallery('next')}
            className="absolute right-4 text-white p-2 hover:bg-white/10 rounded-full transition-colors z-10"
            data-testid="gallery-next-btn"
          >
            <ChevronRight className="w-10 h-10" />
          </button>
          
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {rental.images.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setSelectedImage(idx)}
                className={`w-3 h-3 rounded-full transition-colors ${
                  selectedImage === idx ? 'bg-white' : 'bg-white/40 hover:bg-white/60'
                }`}
              />
            ))}
          </div>
          
          <p className="absolute bottom-12 left-1/2 -translate-x-1/2 text-white/80 text-sm">
            Use arrow keys to navigate - Press ESC to close
          </p>
        </div>
      )}

      {/* Rental Info */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Title & Price */}
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold mb-2" data-testid="rental-title">
                {rental.title}
              </h1>
              <div className="flex items-center text-gray-600 mb-4">
                <MapPin className="w-4 h-4 mr-1" />
                <span>{rental.address}, {rental.location}</span>
              </div>
              <div className="flex items-baseline gap-4 flex-wrap">
                <span className="text-3xl sm:text-4xl font-bold text-primary" data-testid="rental-rent">
                  {formatCurrency(rental.rent)}<span className="text-lg text-gray-500">/month</span>
                </span>
                <span className="text-gray-500">
                  Deposit: {formatCurrency(rental.deposit)}
                </span>
                {rental.maintenance > 0 && (
                  <span className="text-gray-500">
                    Maintenance: {formatCurrency(rental.maintenance)}/mo
                  </span>
                )}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-6 bg-[#F5F5F0] rounded-xl" data-testid="rental-stats">
              {rental.bedrooms && (
                <div className="text-center">
                  <Bed className="w-6 h-6 mx-auto mb-2 text-primary" />
                  <p className="font-semibold">{rental.bedrooms} Beds</p>
                </div>
              )}
              {rental.bathrooms && (
                <div className="text-center">
                  <Bath className="w-6 h-6 mx-auto mb-2 text-primary" />
                  <p className="font-semibold">{rental.bathrooms} Baths</p>
                </div>
              )}
              <div className="text-center">
                <Maximize className="w-6 h-6 mx-auto mb-2 text-primary" />
                <p className="font-semibold">{(rental.area || 0).toLocaleString('en-IN')} sqft</p>
              </div>
              {rental.parking !== undefined && (
                <div className="text-center">
                  <Car className="w-6 h-6 mx-auto mb-2 text-primary" />
                  <p className="font-semibold">{rental.parking} Parking</p>
                </div>
              )}
            </div>

            {/* Description */}
            <div>
              <h2 className="text-xl font-bold mb-4">About this property</h2>
              <p className="text-gray-700 leading-relaxed" data-testid="rental-description">
                {rental.description}
              </p>
            </div>

            {/* Property Details */}
            <div>
              <h2 className="text-xl font-bold mb-4">Property Details</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="rental-details-grid">
                {rental.furnished && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Furnishing</p>
                    <p className="font-semibold">{rental.furnished}</p>
                  </div>
                )}
                {rental.floor && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Floor</p>
                    <p className="font-semibold">{rental.floor} of {rental.total_floors}</p>
                  </div>
                )}
                {rental.age && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Property Age</p>
                    <p className="font-semibold">{rental.age}</p>
                  </div>
                )}
                {rental.facing && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Facing</p>
                    <p className="font-semibold">{rental.facing}</p>
                  </div>
                )}
                {rental.available_from && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Available From</p>
                    <p className="font-semibold">{rental.available_from}</p>
                  </div>
                )}
                {rental.type && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Property Type</p>
                    <p className="font-semibold">{rental.type}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Highlights */}
            {rental.highlights && rental.highlights.length > 0 && (
              <div>
                <h2 className="text-xl font-bold mb-4">Highlights</h2>
                <div className="flex flex-wrap gap-3" data-testid="rental-highlights">
                  {rental.highlights.map((highlight, idx) => (
                    <span key={idx} className="flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium">
                      <CheckCircle className="w-4 h-4" /> {highlight}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Amenities */}
            <div>
              <h2 className="text-xl font-bold mb-4">Amenities</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid="rental-amenities">
                {rental.amenities.map((amenity, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    <span className="text-sm">{amenity}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Contact Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-[#F5F5F0] p-6 rounded-xl space-y-6" data-testid="contact-sidebar">
              <div>
                <h3 className="font-bold text-lg mb-2">Listed by</h3>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold">{rental.owner.name}</p>
                    <p className="text-sm text-gray-500">{rental.owner.type}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Button 
                  onClick={() => setInquiryModalOpen(true)}
                  className="w-full rounded-lg bg-primary py-6"
                  data-testid="inquiry-btn"
                >
                  <Phone className="w-4 h-4 mr-2" /> Send Inquiry
                </Button>
                <Button 
                  variant="outline"
                  className="w-full rounded-lg py-6"
                  data-testid="schedule-visit-btn"
                  onClick={() => setScheduleModalOpen(true)}
                >
                  <Calendar className="w-4 h-4 mr-2" /> Schedule Visit
                </Button>
              </div>

              <div className="pt-4 border-t">
                <p className="text-sm text-gray-500 mb-2">Contact directly:</p>
                <a href={`tel:${rental.owner.phone}`} className="flex items-center gap-2 text-sm hover:text-primary">
                  <Phone className="w-4 h-4" /> {rental.owner.phone}
                </a>
                <a href={`mailto:${rental.owner.email}`} className="flex items-center gap-2 text-sm hover:text-primary mt-2">
                  <Mail className="w-4 h-4" /> {rental.owner.email}
                </a>
              </div>

              {/* Price Summary */}
              <div className="pt-4 border-t">
                <h4 className="font-semibold mb-3">Cost Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Monthly Rent</span>
                    <span className="font-medium">{formatCurrency(rental.rent)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Security Deposit</span>
                    <span className="font-medium">{formatCurrency(rental.deposit)}</span>
                  </div>
                  {rental.maintenance > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Maintenance</span>
                      <span className="font-medium">{formatCurrency(rental.maintenance)}/mo</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t font-semibold">
                    <span>Move-in Cost</span>
                    <span className="text-primary">{formatCurrency(rental.rent + rental.deposit + (rental.maintenance || 0))}</span>
                  </div>
                </div>
              </div>

              {/* WhatsApp CTA */}
              <Button 
                variant="outline"
                className="w-full rounded-lg border-green-500 text-green-600 hover:bg-green-50"
                onClick={() => {
                  const msg = `Hi! I'm interested in: ${rental.title} at ${rental.location}. Rent: ${formatCurrency(rental.rent)}/month`;
                  window.open(`https://wa.me/918790460259?text=${encodeURIComponent(msg)}`, '_blank');
                }}
                data-testid="whatsapp-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Chat on WhatsApp
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Similar Properties CTA */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl font-bold mb-4">Looking for More Options?</h2>
          <p className="text-gray-600 mb-6">Browse our complete collection of rental properties</p>
          <Link to="/rentals/find">
            <Button className="rounded-full bg-primary px-8">
              View All Rentals <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Inquiry Modal */}
      <Dialog open={inquiryModalOpen} onOpenChange={setInquiryModalOpen}>
        <DialogContent className="sm:max-w-[500px]" data-testid="inquiry-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-primary" />
              Send Inquiry
            </DialogTitle>
            <DialogDescription>
              Send your inquiry about this rental property. We'll get back to you shortly.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInquirySubmit} className="space-y-4 mt-4">
            <div>
              <Label>Your Name *</Label>
              <Input
                value={inquiryForm.name}
                onChange={(e) => setInquiryForm({...inquiryForm, name: e.target.value})}
                placeholder="Enter your name"
                required
                className="mt-1"
                data-testid="inquiry-name-input"
              />
            </div>
            <div>
              <Label>Phone Number *</Label>
              <Input
                type="tel"
                value={inquiryForm.phone}
                onChange={(e) => setInquiryForm({...inquiryForm, phone: e.target.value})}
                placeholder="+91 98765 43210"
                required
                className="mt-1"
                data-testid="inquiry-phone-input"
              />
            </div>
            <div>
              <Label>Email (Optional)</Label>
              <Input
                type="email"
                value={inquiryForm.email}
                onChange={(e) => setInquiryForm({...inquiryForm, email: e.target.value})}
                placeholder="you@email.com"
                className="mt-1"
                data-testid="inquiry-email-input"
              />
            </div>
            <div>
              <Label>Message (Optional)</Label>
              <Textarea
                value={inquiryForm.message}
                onChange={(e) => setInquiryForm({...inquiryForm, message: e.target.value})}
                placeholder="I'm interested in this rental property..."
                rows={3}
                className="mt-1"
                data-testid="inquiry-message-input"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setInquiryModalOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-primary" data-testid="inquiry-submit-btn">
                Send Inquiry
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Schedule Visit Modal */}
      <Dialog open={scheduleModalOpen} onOpenChange={setScheduleModalOpen}>
        <DialogContent className="sm:max-w-[500px]" data-testid="schedule-visit-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              Schedule a Visit
            </DialogTitle>
            <DialogDescription>
              Book a time to visit this rental property. We'll confirm your appointment.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleScheduleSubmit} className="space-y-4 mt-4">
            <div>
              <Label>Your Name *</Label>
              <Input
                value={scheduleForm.name}
                onChange={(e) => setScheduleForm({...scheduleForm, name: e.target.value})}
                placeholder="Enter your name"
                required
                className="mt-1"
                data-testid="schedule-name-input"
              />
            </div>
            <div>
              <Label>Phone Number *</Label>
              <Input
                type="tel"
                value={scheduleForm.phone}
                onChange={(e) => setScheduleForm({...scheduleForm, phone: e.target.value})}
                placeholder="+91 98765 43210"
                required
                className="mt-1"
                data-testid="schedule-phone-input"
              />
            </div>
            <div>
              <Label>Email (Optional)</Label>
              <Input
                type="email"
                value={scheduleForm.email}
                onChange={(e) => setScheduleForm({...scheduleForm, email: e.target.value})}
                placeholder="you@email.com"
                className="mt-1"
                data-testid="schedule-email-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Preferred Date *</Label>
                <Input
                  type="date"
                  value={scheduleForm.preferred_date}
                  onChange={(e) => setScheduleForm({...scheduleForm, preferred_date: e.target.value})}
                  required
                  className="mt-1"
                  min={new Date().toISOString().split('T')[0]}
                  data-testid="schedule-date-input"
                />
              </div>
              <div>
                <Label>Preferred Time</Label>
                <Input
                  type="time"
                  value={scheduleForm.preferred_time}
                  onChange={(e) => setScheduleForm({...scheduleForm, preferred_time: e.target.value})}
                  className="mt-1"
                  data-testid="schedule-time-input"
                />
              </div>
            </div>
            <div>
              <Label>Additional Notes (Optional)</Label>
              <Textarea
                value={scheduleForm.notes}
                onChange={(e) => setScheduleForm({...scheduleForm, notes: e.target.value})}
                placeholder="Any specific requirements or questions..."
                rows={2}
                className="mt-1"
                data-testid="schedule-notes-input"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setScheduleModalOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-primary" data-testid="schedule-submit-btn">
                Schedule Visit
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RentalDetails;
